import httpClient from './httpClient';
import { WORKSPACE_VISIBILITY } from '../pages/Onboarding/WorkspaceOnboarding';
import { getCurrentlyActiveOrganization } from '../common/auth';
import { AxiosResponse } from 'axios';
import Workspace, { WorkspaceDefaultModule, WorkspaceFramework } from '../types/Workspace';
import { User } from '../types/User';
import { WorkspaceAccess } from '../types/WorkspaceAccess';
import { Epic } from '../types/Epic';
import { TaskStatus } from '../types/TaskStatus';
import { SpaceLabel } from '../types/TaskLabel';
import { MigrationStatusMapping } from '../pages/Space/SpaceSettingsModal/SpaceTasksMigration';
import { Filter } from '../components/Modal/TaskFiltersModal';
import { UpdateSpaceTasksModel } from './types/workspace.types';
import {
  CreateLabelRequest,
  UpdateLabelRequest,
  UpdateLabelsOrderRequest,
  AddLabelsToTaskRequest,
  UpdateTaskLabelsRequest,
} from './types/label.types';
import {
  WorkspaceView,
  WorkspaceViewEntityType,
  WorkspaceViewGroup,
  WorkspaceViewRepresentation,
} from '../types/WorkspaceView';
import { WorkspaceViewColumn } from '../types/WorkspaceViewColumn';
import { WorkspaceViewSort } from '../types/WorkspaceViewSortBy';

const WORKSPACE_BASE_ROUTE = '/v1/workspace';

export interface CreateWorkspaceResponse {
  id: number;
  publicId: string;
  name: string;
}

/**
 * Create new workspace under selected organization
 * @param {string} name
 * @param {WORKSPACE_VISIBILITY} visibility
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export async function createWorkspace(
  name: string,
  visibility: WORKSPACE_VISIBILITY,
  framework: WorkspaceFramework
): Promise<AxiosResponse<CreateWorkspaceResponse>> {
  return await httpClient({
    method: 'POST',
    url: `${WORKSPACE_BASE_ROUTE}/`,
    data: {
      name,
      visibility,
      organizationId: (await getCurrentlyActiveOrganization()).id,
      framework: framework,
    },
  });
}

export async function getWorkspaceById(workspaceId: number | string): Promise<AxiosResponse<Workspace>> {
  return httpClient({
    method: 'GET',
    url: `${WORKSPACE_BASE_ROUTE}/${workspaceId}`,
  });
}

export interface WorkspaceSprintSummary {
  id: number;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
}

export async function getWorkspaceSprints(
  spaceId: number
): Promise<AxiosResponse<{ sprints: WorkspaceSprintSummary[] }>> {
  return httpClient({
    method: 'GET',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/sprints`,
  });
}

/**
 * Return all the workspaces user has access to. Including public org wide and private.
 * @param orgId
 */
export async function getUserWorkspaces(orgId: number): Promise<AxiosResponse<Workspace[]>> {
  return httpClient({
    method: 'GET',
    url: `${WORKSPACE_BASE_ROUTE}/?orgId=${orgId}`,
  });
}

export interface DiscoverableWorkspace {
  id: number;
  publicId: string;
  name: string;
  framework: WorkspaceFramework;
  visibility: string;
  memberCount: number;
}

/**
 * Return public spaces in the organization the user has not joined yet (for discovery).
 * @param orgId
 */
export async function getDiscoverableWorkspaces(orgId: number): Promise<AxiosResponse<DiscoverableWorkspace[]>> {
  return httpClient({
    method: 'GET',
    url: `${WORKSPACE_BASE_ROUTE}/discoverable?orgId=${orgId}`,
  });
}

/**
 * Self-join a public space. Accepts numeric id or publicId.
 * @param spaceId
 */
export async function joinWorkspace(spaceId: number | string): Promise<AxiosResponse<Workspace>> {
  return httpClient({
    method: 'POST',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/join`,
  });
}

export interface WorkspaceAccessResponse extends WorkspaceAccess {
  User: User;
  userId: number;
}

export async function getUsersWithAccessToSpace(
  workspaceId: number
): Promise<AxiosResponse<WorkspaceAccessResponse[]>> {
  return httpClient({
    method: 'GET',
    url: `${WORKSPACE_BASE_ROUTE}/${workspaceId}/access`,
  });
}

/**
 * Delete space with a given ID
 * @param workspaceId
 */
export async function deleteSpace(workspaceId: number): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'DELETE',
    url: `${WORKSPACE_BASE_ROUTE}/${workspaceId}`,
  });
}

/**
 * Leave space with a given ID
 * @param workspaceId
 */
export async function leaveSpace(workspaceId: number): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'DELETE',
    url: `${WORKSPACE_BASE_ROUTE}/${workspaceId}/leave`,
  });
}

export interface UpdateSpaceSettingsModel {
  name?: string;
  visibility?: WORKSPACE_VISIBILITY;
  framework?: WorkspaceFramework;
  defaultModule?: WorkspaceDefaultModule;
  statusesMapping?: MigrationStatusMapping;
  filters?: Filter[];
  estimationType?: string;
}

export function updateSpaceSettings(
  spaceId: number,
  updateModel: UpdateSpaceSettingsModel
): Promise<AxiosResponse<Workspace>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}`,
    data: updateModel,
  });
}

export function addUserToWorkspace(
  userEmail: string,
  spaceId: string
): Promise<AxiosResponse<WorkspaceAccessResponse>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/grant-access/${userEmail}`,
  });
}

export function revokeSpaceAccess(userEmail: string, spaceId: string): Promise<AxiosResponse<WorkspaceAccessResponse>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/revoke-access/${userEmail}`,
  });
}

export function editUserSpaceRole(
  userEmail: string,
  spaceId: string,
  newRole: string
): Promise<AxiosResponse<WorkspaceAccessResponse>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/update-role/${userEmail}`,
    data: {
      newRole,
    },
  });
}

export function claimSpaceOwnership(spaceId: string): Promise<AxiosResponse<WorkspaceAccessResponse>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/claim-ownership`,
  });
}

// update task order
export function updateTaskOrder(spaceId: number, taskOrder: number[]): Promise<AxiosResponse<Epic>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/task-order`,
    data: {
      taskOrder,
    },
  });
}

// create Epic in workspace
export function createEpic(spaceId: number, name: string): Promise<AxiosResponse<Epic>> {
  return httpClient({
    method: 'POST',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/epic`,
    data: {
      name,
    },
  });
}

// get epics
export async function getEpics(spaceId: number): Promise<Epic[]> {
  const response = await httpClient({
    method: 'GET',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/epic`,
  });
  return response.data;
}

// update epic
export function updateEpic(spaceId: number, epicId: number, epic: Partial<Epic>): Promise<AxiosResponse<Epic>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/epic/${epicId}`,
    data: {
      epic: epic,
    },
  });
}

// update epic
export function updateEpicsOrder(spaceId: number, epicIds: Array<number>): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/epic-order`,
    data: {
      epicIds,
    },
  });
}

// delete epic
export function deleteEpic(spaceId: number, epicId: number): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'DELETE',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/epic/${epicId}`,
  });
}
export interface TaskOrder {
  statusId: number;
  tasks: number[];
}

// createSprint - for above route
export function createSprint(
  name: string,
  startDate: Date,
  endDate: Date | undefined,
  spaceId: number,
  planningSessionId: number,
  taskIds: number[],
  taskOrder: TaskOrder[]
): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'POST',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/sprint`,
    data: {
      name,
      startDate,
      endDate,
      planningSessionId,
      taskIds,
      taskOrder,
    },
  });
}

// getSprint - for above route
export function getSprint(spaceId: number, sprintId: number): Promise<AxiosResponse<{ sprint: any }>> {
  return httpClient({
    method: 'GET',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/sprint/${sprintId}`,
  });
}

export const updateSprint = async (spaceId: number, sprintId: number, name: string) => {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/sprint/${sprintId}`,
    data: {
      name: name,
    },
  });
};

// getSprintsTasks - for above route
export function getSprintTasks(sprintId: number): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'GET',
    url: `${WORKSPACE_BASE_ROUTE}/sprint/${sprintId}/tasks`,
  });
}

export function endSprint(
  sprintId: number,
  moveUnfinishedTasksToPlanning = false
): Promise<AxiosResponse<{ message: string; status: number }>> {
  return httpClient({
    method: 'PUT',
    url: `${WORKSPACE_BASE_ROUTE}/end-sprint/${sprintId}${moveUnfinishedTasksToPlanning ? '?moveUnfinishedTasksToPlanning=true' : ''}`,
  });
}

export const addTaskStatus = async (spaceId: number, taskStatus: Partial<TaskStatus>) => {
  return httpClient({
    method: 'POST',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/add-task-status`,
    data: {
      ...taskStatus,
    },
  });
};

export const updateTaskStatus = async (spaceId: number, taskStatus: TaskStatus) => {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/task-status/${taskStatus.id}`,
    data: {
      taskStatus,
    },
  });
};

export const updateTaskStatusesOrder = async (spaceId: number, taskStatusesOrder: Record<number, number>) => {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/task-statuses/order`,
    data: {
      taskStatusesOrder,
    },
  });
};

export const deleteTaskStatus = async (
  spaceId: number,
  taskStatusId: number,
  replacingTaskStatusId: number | undefined
) => {
  return httpClient({
    method: 'DELETE',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/task-status/${taskStatusId}`,
    data: {
      replacingTaskStatusId,
    },
  });
};

export const setDefaultTaskStatus = async (spaceId: number, taskStatusId: number) => {
  return httpClient({
    method: 'POST',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/task-status-set-default/${taskStatusId}`,
  });
};

export function deleteTasks(spaceId: number, tasks: number[]): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'DELETE',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/tasks`,
    data: {
      tasks: tasks,
    },
  });
}

export type UpdateSpaceTasksResponse = {
  tasks: number[];
};

export function updateTasks(
  spaceId: number,
  tasks: number[],
  updateTasksModel: UpdateSpaceTasksModel
): Promise<AxiosResponse<UpdateSpaceTasksResponse>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/tasks/update`,
    data: {
      tasks: tasks,
      ...updateTasksModel,
    },
  });
}

export function createView(
  spaceId: number,
  name: string,
  group: WorkspaceViewGroup,
  representation: WorkspaceViewRepresentation,
  entityType: WorkspaceViewEntityType,
  groupBy: string,
  columns: WorkspaceViewColumn,
  sortBy: WorkspaceViewSort
): Promise<AxiosResponse<WorkspaceView>> {
  return httpClient({
    method: 'POST',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/view`,
    data: {
      name,
      group,
      representation,
      entityType,
      groupBy,
      columns,
      sortBy,
    },
  });
}

export interface UpdateWorkspaceViewModel {
  name?: string;
  group?: WorkspaceViewGroup;
  representation?: WorkspaceViewRepresentation;
  groupBy?: string;
  filters?: Filter[];
  columns?: WorkspaceViewColumn;
  entityType?: WorkspaceViewEntityType;
}

export function updateWorkspaceView(
  spaceId: number,
  viewId: number,
  updateModel: UpdateWorkspaceViewModel
): Promise<AxiosResponse<WorkspaceView>> {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/view/${viewId}`,
    data: updateModel,
  });
}

export const updateWorkspaceViewsOrder = async (spaceId: number, viewsOrder: Record<number, number>) => {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/views/order`,
    data: {
      viewsOrder,
    },
  });
};

export const setDefaultView = async (spaceId: number, viewId: number) => {
  return httpClient({
    method: 'POST',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/view-set-default/${viewId}`,
  });
};

export function deleteView(spaceId: number, viewId: number): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'DELETE',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/view/${viewId}`,
  });
}

// Label management functions
export const getWorkspaceLabels = async (workspaceId: number): Promise<AxiosResponse<SpaceLabel[]>> => {
  return httpClient({
    method: 'GET',
    url: `${WORKSPACE_BASE_ROUTE}/${workspaceId}/labels`,
  });
};

export const addLabel = async (spaceId: number, label: CreateLabelRequest): Promise<AxiosResponse<SpaceLabel>> => {
  return httpClient({
    method: 'POST',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/add-label`,
    data: label,
  });
};

export const updateLabel = async (
  spaceId: number,
  labelId: number,
  label: UpdateLabelRequest
): Promise<AxiosResponse<SpaceLabel>> => {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/label/${labelId}`,
    data: label,
  });
};

export const deleteLabel = async (spaceId: number, labelId: number): Promise<AxiosResponse<void>> => {
  return httpClient({
    method: 'DELETE',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/label/${labelId}`,
  });
};

export const updateLabelsOrder = async (
  spaceId: number,
  data: UpdateLabelsOrderRequest
): Promise<AxiosResponse<void>> => {
  return httpClient({
    method: 'PATCH',
    url: `${WORKSPACE_BASE_ROUTE}/${spaceId}/labels/order`,
    data,
  });
};
